/**
 * Generates non-overlapping orthogonal paths on a grid for an "arrow puzzle" admin preview / persisted game.
 * Correct paths end on the border with an outward-pointing arrow; wrong paths stay in the interior.
 */

const key = (r, c) => `${r},${c}`;

/** Editor and runtime clamp so labels stay readable and cannot blow up the SVG layout. */
export const ARROW_LABEL_FONT_SCALE_MIN = 0.2;
export const ARROW_LABEL_FONT_SCALE_MAX = 5;

/**
 * Coerces `labelFontScale`: finite, positive, clamped to {@link ARROW_LABEL_FONT_SCALE_MIN}…{@link ARROW_LABEL_FONT_SCALE_MAX}.
 */
export function coerceLabelFontScale(n) {
  const x = Number(n);
  if (!Number.isFinite(x) || x <= 0) return 1;
  return Math.min(ARROW_LABEL_FONT_SCALE_MAX, Math.max(ARROW_LABEL_FONT_SCALE_MIN, x));
}

/** Degrees in [-180, 180] for `labelRotationDeg`. */
export function normalizeLabelRotationDeg(deg) {
  let d = Number(deg);
  if (Number.isNaN(d)) return 0;
  d = ((d % 360) + 360) % 360;
  if (d > 180) d -= 360;
  return d;
}

/**
 * Ensure saved puzzles have label layout fields (position + font scale).
 * @param {object|null} puzzle
 */
export function normalizeArrowPuzzle(puzzle) {
  if (!puzzle || typeof puzzle !== "object" || !Array.isArray(puzzle.paths)) return puzzle;
  return {
    ...puzzle,
    paths: puzzle.paths.map((p) => {
      const s = coerceLabelFontScale(p.labelFontScale != null ? p.labelFontScale : 1);
      return {
        ...p,
        labelDxCells: p.labelDxCells != null && !Number.isNaN(Number(p.labelDxCells)) ? Number(p.labelDxCells) : 0,
        labelDyCells: p.labelDyCells != null && !Number.isNaN(Number(p.labelDyCells)) ? Number(p.labelDyCells) : 0,
        labelFontScale: s,
        labelRotationDeg: normalizeLabelRotationDeg(p.labelRotationDeg != null ? p.labelRotationDeg : 0),
      };
    }),
  };
}

/** One saved puzzle (object or JSON string). */
export function parseArrowPuzzleValue(raw) {
  if (!raw) return null;
  let o = raw;
  if (typeof raw === "string") {
    try {
      o = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!o || typeof o !== "object" || !Array.isArray(o.paths)) return null;
  return o;
}

/** Map categoryKey (any case) → normalized puzzle. */
export function parseArrowPuzzlesMap(raw) {
  if (!raw) return {};
  let o = raw;
  if (typeof raw === "string") {
    try {
      o = JSON.parse(raw);
    } catch {
      return {};
    }
  }
  if (!o || typeof o !== "object" || Array.isArray(o)) return {};
  const out = {};
  for (const [k, v] of Object.entries(o)) {
    const p = parseArrowPuzzleValue(v);
    if (p && Array.isArray(p.paths)) out[String(k).toUpperCase()] = normalizeArrowPuzzle(p);
  }
  return out;
}

/**
 * Puzzle for a category from theme data: prefers `arrow_puzzles[cat]`, then legacy `arrow_puzzle` if it matches.
 * @param {{ arrow_puzzles?: object, arrow_puzzle?: object|string }} themeData
 * @param {string} categoryKeyUpper
 */
export function getArrowPuzzleForCategory(themeData, categoryKeyUpper) {
  if (!themeData || !categoryKeyUpper) return null;
  const key = String(categoryKeyUpper).toUpperCase();
  const map = parseArrowPuzzlesMap(themeData.arrow_puzzles);
  if (map[key]?.paths?.length) return map[key];
  const leg = parseArrowPuzzleValue(themeData.arrow_puzzle);
  if (!leg?.paths?.length) return null;
  const ck = leg.categoryKey != null && String(leg.categoryKey).trim() ? String(leg.categoryKey).toUpperCase() : null;
  if (ck === key) return normalizeArrowPuzzle(leg);
  if (!ck && !Object.keys(map).length) return normalizeArrowPuzzle({ ...leg, categoryKey: key });
  return null;
}

export function parseGridSize(gridSelected) {
  const m = String(gridSelected || "").match(/(\d+)\s*x\s*(\d+)/i);
  if (m) return { width: Math.max(2, parseInt(m[1], 10)), height: Math.max(2, parseInt(m[2], 10)) };
  return { width: 10, height: 10 };
}

function isBorder(r, c, w, h) {
  return r === 0 || c === 0 || r === h - 1 || c === w - 1;
}

function inBounds(r, c, w, h) {
  return r >= 0 && r < h && c >= 0 && c < w;
}

const DELTAS = [
  [-1, 0, "up"],
  [1, 0, "down"],
  [0, -1, "left"],
  [0, 1, "right"],
];

function neighbors4(r, c, w, h) {
  const out = [];
  for (const [dr, dc, dname] of DELTAS) {
    const nr = r + dr;
    const nc = c + dc;
    if (inBounds(nr, nc, w, h)) out.push([nr, nc, dname]);
  }
  return out;
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function dirBetween(from, to) {
  const dr = to[0] - from[0];
  const dc = to[1] - from[1];
  if (dr === -1 && dc === 0) return "up";
  if (dr === 1 && dc === 0) return "down";
  if (dr === 0 && dc === -1) return "left";
  if (dr === 0 && dc === 1) return "right";
  return "right";
}

/** All border cells with inward neighbor and outward arrow name. */
function edgeEndpoints(w, h) {
  const edges = [];
  for (let c = 0; c < w; c++) {
    edges.push({ end: [0, c], inward: [1, c], out: "up" });
    edges.push({ end: [h - 1, c], inward: [h - 2, c], out: "down" });
  }
  for (let r = 0; r < h; r++) {
    edges.push({ end: [r, 0], inward: [r, 1], out: "left" });
    edges.push({ end: [r, w - 1], inward: [r, w - 2], out: "right" });
  }
  return edges;
}

function randomInt(a, b) {
  return a + Math.floor(Math.random() * (b - a + 1));
}

/**
 * Category keys (uppercase) that have at least one non-empty correct and one non-empty wrong in catItems.
 */
export function getCategoryKeysWithUsableWords(catItems, categoryKeysUpper) {
  const keys = Array.isArray(categoryKeysUpper)
    ? [...new Set(categoryKeysUpper.map((k) => String(k).toUpperCase()))]
    : [];
  return keys.filter((cat) => {
    const item = catItems && typeof catItems === "object" ? catItems[cat] : null;
    if (!item || typeof item !== "object") return false;
    const hasC = (Array.isArray(item.correct) ? item.correct : []).some((w) => String(w || "").trim());
    const hasW = (Array.isArray(item.wrong) ? item.wrong : []).some((w) => String(w || "").trim());
    return hasC && hasW;
  });
}

/** Collect correct/wrong word entries from cat_items for given category keys (e.g. default_cat). */
export function collectWordsFromCatItems(catItems, categoryKeys) {
  const correct = [];
  const wrong = [];
  const cats = Array.isArray(categoryKeys) ? categoryKeys.map((c) => String(c).toUpperCase()) : [];
  for (const cat of cats) {
    const item = catItems && typeof catItems === "object" ? catItems[cat] : null;
    if (!item || typeof item !== "object") continue;
    (Array.isArray(item.correct) ? item.correct : []).forEach((w) => {
      const t = String(w || "").trim();
      if (t) correct.push({ word: t, category: cat });
    });
    (Array.isArray(item.wrong) ? item.wrong : []).forEach((w) => {
      const t = String(w || "").trim();
      if (t) wrong.push({ word: t, category: cat });
    });
  }
  return { correct, wrong };
}

const OPPOSITE = { up: "down", down: "up", left: "right", right: "left" };

/**
 * True when the candidate's arrow head looks straight along its direction (through empty cells only)
 * at another path's head that points back at it (two arrows facing each other).
 */
function facesAnotherArrow(cells, arrow, paths, w, h) {
  if (!paths || !paths.length) return false;
  const [dr, dc] = DELTAS.find(([, , n]) => n === arrow);
  const own = new Set(cells.map(([r, c]) => key(r, c)));
  const heads = new Map();
  const body = new Set();
  for (const p of paths) {
    p.cells.forEach(([r, c], i) => {
      if (i === p.cells.length - 1) heads.set(key(r, c), p.arrow);
      else body.add(key(r, c));
    });
  }
  const last = cells[cells.length - 1];
  let r = last[0] + dr;
  let c = last[1] + dc;
  while (inBounds(r, c, w, h)) {
    const k = key(r, c);
    if (own.has(k) || body.has(k)) return false;
    if (heads.has(k)) return heads.get(k) === OPPOSITE[arrow];
    r += dr;
    c += dc;
  }
  return false;
}

/**
 * True when every path can be tapped out in some order. Mirrors the game: a path may slide only if
 * no other remaining path occupies a cell on its head's straight ray to the board edge.
 */
function isSolvable(paths, w, h) {
  const remaining = paths.map((_, i) => i);
  const cellSets = paths.map((p) => new Set(p.cells.map(([r, c]) => key(r, c))));
  const rays = paths.map((p) => {
    const [dr, dc] = DELTAS.find(([, , n]) => n === p.arrow);
    const head = p.cells[p.cells.length - 1];
    const ray = [];
    for (let r = head[0] + dr, c = head[1] + dc; inBounds(r, c, w, h); r += dr, c += dc) ray.push(key(r, c));
    return ray;
  });
  let list = remaining;
  while (list.length) {
    const free = list.filter((i) => !list.some((j) => j !== i && rays[i].some((k) => cellSets[j].has(k))));
    if (!free.length) return false;
    list = list.filter((i) => !free.includes(i));
  }
  return true;
}

/** Candidate would make two arrows face each other, or leave the puzzle unsolvable. */
function rejectsCandidate(cells, arrow, paths, w, h) {
  if (facesAnotherArrow(cells, arrow, paths, w, h)) return true;
  return !isSolvable([...(paths || []), { cells, arrow }], w, h);
}

/** Short interior-only paths for visual density (no word label). */
function tryGrowExtraPath(w, h, occupied, minLen, maxLen, paths) {
  const interior = [];
  for (let r = 1; r < h - 1; r++) {
    for (let c = 1; c < w - 1; c++) {
      if (!occupied.has(key(r, c))) interior.push([r, c]);
    }
  }
  if (interior.length < minLen) return null;

  for (let attempt = 0; attempt < 500; attempt++) {
    const targetLen = randomInt(minLen, Math.min(maxLen, interior.length));
    const start = interior[Math.floor(Math.random() * interior.length)];
    if (occupied.has(key(start[0], start[1]))) continue;

    const path = [start];
    const seen = new Set([key(start[0], start[1])]);
    let cur = start;

    while (path.length < targetLen) {
      const cand = shuffle(neighbors4(cur[0], cur[1], w, h)).filter(
        ([nr, nc]) => !isBorder(nr, nc, w, h) && !occupied.has(key(nr, nc)) && !seen.has(key(nr, nc))
      );
      if (cand.length === 0) break;
      const [nr, nc] = cand[0];
      path.push([nr, nc]);
      seen.add(key(nr, nc));
      cur = [nr, nc];
    }

    if (path.length < minLen) continue;

    const prev = path.length >= 2 ? path[path.length - 2] : null;
    const last = path[path.length - 1];
    const arrow = prev ? dirBetween(prev, last) : "right";

    let fr = last[0], fc = last[1];
    if (arrow === "up") fr--;
    if (arrow === "down") fr++;
    if (arrow === "left") fc--;
    if (arrow === "right") fc++;

    if (!inBounds(fr, fc, w, h) || occupied.has(key(fr, fc)) || path.some(([pr, pc]) => pr === fr && pc === fc)) {
      continue;
    }

    if (rejectsCandidate(path, arrow, paths, w, h)) continue;

    path.forEach(([r, c]) => occupied.add(key(r, c)));
    occupied.add(key(fr, fc));

    return { cells: path, arrow, kind: "extra" };
  }
  return null;
}

function tryGrowWrongPath(w, h, occupied, minLen, maxLen, paths) {
  const interior = [];
  for (let r = 1; r < h - 1; r++) {
    for (let c = 1; c < w - 1; c++) {
      if (!occupied.has(key(r, c))) interior.push([r, c]);
    }
  }
  if (interior.length < minLen) return null;

  for (let attempt = 0; attempt < 400; attempt++) {
    const targetLen = randomInt(minLen, Math.min(maxLen, interior.length));
    const start = interior[Math.floor(Math.random() * interior.length)];
    if (occupied.has(key(start[0], start[1]))) continue;

    const path = [start];
    const seen = new Set([key(start[0], start[1])]);
    let cur = start;

    while (path.length < targetLen) {
      const cand = shuffle(neighbors4(cur[0], cur[1], w, h)).filter(
        ([nr, nc]) => !isBorder(nr, nc, w, h) && !occupied.has(key(nr, nc)) && !seen.has(key(nr, nc))
      );
      if (cand.length === 0) break;
      const [nr, nc] = cand[0];
      path.push([nr, nc]);
      seen.add(key(nr, nc));
      cur = [nr, nc];
    }

    if (path.length < minLen) continue;

    const prev = path.length >= 2 ? path[path.length - 2] : null;
    const last = path[path.length - 1];
    const arrow = prev ? dirBetween(prev, last) : "right";

    let fr = last[0], fc = last[1];
    if (arrow === "up") fr--;
    if (arrow === "down") fr++;
    if (arrow === "left") fc--;
    if (arrow === "right") fc++;

    if (!inBounds(fr, fc, w, h) || occupied.has(key(fr, fc)) || path.some(([pr, pc]) => pr === fr && pc === fc)) {
      continue;
    }

    if (rejectsCandidate(path, arrow, paths, w, h)) continue;

    path.forEach(([r, c]) => occupied.add(key(r, c)));
    occupied.add(key(fr, fc));

    return { cells: path, arrow, kind: "wrong" };
  }
  return null;
}

function tryGrowCorrectPath(w, h, occupied, minLen, maxLen, paths) {
  const edges = shuffle(edgeEndpoints(w, h));
  for (const { end, inward, out } of edges) {
    const [er, ec] = end;
    const [inr, inc] = inward;
    if (!inBounds(inr, inc, w, h)) continue;
    if (occupied.has(key(er, ec)) || occupied.has(key(inr, inc))) continue;

    const targetLen = randomInt(minLen, maxLen);
    // Order: cells[0] = deep interior start → cells[last] = border cell (arrow `out` points off-grid).
    const cells = [inward, end];
    const seen = new Set([key(inr, inc), key(er, ec)]);
    let cur = inward;

    while (cells.length < targetLen) {
      const cand = shuffle(neighbors4(cur[0], cur[1], w, h)).filter(([nr, nc]) => {
        if (seen.has(key(nr, nc))) return false;
        if (occupied.has(key(nr, nc))) return false;
        return true;
      });
      if (cand.length === 0) break;
      const [nr, nc] = cand[0];
      cells.unshift([nr, nc]);
      seen.add(key(nr, nc));
      cur = [nr, nc];
    }

    if (cells.length < minLen) continue;
    if (rejectsCandidate(cells, out, paths, w, h)) continue;

    cells.forEach(([r, c]) => occupied.add(key(r, c)));
    return { cells, arrow: out, kind: "correct" };
  }
  return null;
}

/**
 * @param {object} opts
 * @param {number} opts.width
 * @param {number} opts.height
 * @param {Array<{word:string,category?:string}>} opts.correctWords
 * @param {Array<{word:string,category?:string}>} opts.wrongWords
 * @param {boolean} opts.wordless
 * @param {string} [opts.categoryKey] uppercase category this puzzle was built from (stored on save)
 * @returns {{ version: number, width: number, height: number, wordless_mode: boolean, generatedAt: string, paths: object[], categoryKey?: string }}
 */
export function generateArrowPuzzle({ width, height, correctWords, wrongWords, wordless, categoryKey }) {
  const w = Math.max(3, Math.min(40, width));
  const h = Math.max(3, Math.min(40, height));
  const occupied = new Set();

  const cw = wordless ? [] : shuffle([...(correctWords || [])]);
  const ww = wordless ? [] : shuffle([...(wrongWords || [])]);

  const area = w * h;
  /** More word-bearing paths on larger grids. */
  const maxPaths = Math.max(4, Math.min(40, Math.floor(area / 7)));
  let wrongTarget;
  let correctTarget;
  if (wordless) {
    wrongTarget = Math.min(14, Math.ceil(maxPaths / 2));
    correctTarget = Math.min(14, maxPaths - wrongTarget);
  } else {
    wrongTarget = Math.min(ww.length, Math.ceil(maxPaths / 2));
    correctTarget = Math.min(cw.length, Math.max(0, maxPaths - wrongTarget));
  }

  const minLen = Math.max(3, Math.min(5, Math.floor(Math.min(w, h) / 3)));
  const maxLen = Math.max(minLen + 1, Math.min(16, Math.floor(Math.min(w, h) * 0.85)));

  // If there are very few words provided, we still want a fully populated "complex" maze 
  // matching the requested grid size. We drastically boost the budget and length for unlabeled filler paths.
  const baseTarget = wrongTarget + correctTarget;
  const remainingBudget = Math.max(0, maxPaths - baseTarget);
  
  /** Extra decorative interior lines (no labels) forced to use full maze length to maintain puzzle complexity universally. */
  const extraBudget = Math.min(45, Math.max(10, Math.floor(area / 10))) + remainingBudget;
  const extraMin = minLen; 
  const extraMax = maxLen;

  const paths = [];
  let wi = 0;
  let ci = 0;

  // Wrong first (interior coils), then correct (to border).
  for (let n = 0; n < wrongTarget; n++) {
    const p = tryGrowWrongPath(w, h, occupied, minLen, maxLen, paths);
    if (!p) break;
    const label = wordless ? `W${n + 1}` : ww[wi]?.word || `W${n + 1}`;
    const category = wordless ? "" : ww[wi]?.category || "";
    wi++;
    paths.push({
      ...p,
      label,
      category,
      labelDxCells: 0,
      labelDyCells: 0,
      labelFontScale: 1,
      labelRotationDeg: 0,
    });
  }

  for (let n = 0; n < correctTarget; n++) {
    const p = tryGrowCorrectPath(w, h, occupied, minLen, maxLen, paths);
    if (!p) break;
    const label = wordless ? `C${n + 1}` : cw[ci]?.word || `C${n + 1}`;
    const category = wordless ? "" : cw[ci]?.category || "";
    ci++;
    paths.push({
      ...p,
      label,
      category,
      labelDxCells: 0,
      labelDyCells: 0,
      labelFontScale: 1,
      labelRotationDeg: 0,
    });
  }

  if (paths.length === 0) {
    throw new Error("Could not place any paths — try a larger grid or regenerate.");
  }

  let placedExtra = 0;
  for (let e = 0; e < extraBudget; e++) {
    const ex = tryGrowExtraPath(w, h, occupied, extraMin, extraMax, paths);
    if (!ex) break;
    paths.push({
      ...ex,
      label: "",
      category: "",
      labelDxCells: 0,
      labelDyCells: 0,
      labelFontScale: 1,
      labelRotationDeg: 0,
    });
    placedExtra++;
  }

  const out = {
    version: 2,
    width: w,
    height: h,
    wordless_mode: Boolean(wordless),
    generatedAt: new Date().toISOString(),
    paths,
    extraLineCount: placedExtra,
  };
  if (categoryKey != null && String(categoryKey).trim()) {
    out.categoryKey = String(categoryKey).toUpperCase();
  }
  return out;
}
